'use client';

import { faExternalLink, faUserCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import CustomLink from '../ui/custom-link';

import NotificationsPopup from './Notification/NotificationsPopup';
import { useUser } from './UserInfoProvider';
import { UserProfile } from './UserProfile';
import { Application } from './utils/nav.config';
import { NotificationLookup } from './utils/types';

export interface TopNavigationProps {
	notifications: NotificationLookup | undefined;
	canAccessManage: boolean;
	application: Application;
	pickleballHref: string;
	tournamentHref: string;
	leaguesHref: string;
	clubsHref: string;
	teamLeaguesHref: string;
	cdnUrl: string;
	checkoutLink: string;
	ssoLink: string;
	onSSOClick?: () => void;
}

export function TopNavigation({
	notifications,
	canAccessManage,
	application,
	pickleballHref,
	tournamentHref,
	leaguesHref,
	clubsHref,
	teamLeaguesHref,
	cdnUrl,
	checkoutLink,
	ssoLink,
	onSSOClick
}: TopNavigationProps) {
	const user = useUser();
	const pathname = usePathname();
	const [baseURL, setBaseURL] = useState('');

	useEffect(() => {
		setBaseURL(window.location.href);
	}, []);

	const isLocalHost = pickleballHref.includes('localhost') || tournamentHref.includes('localhost');

	const isTourneyPage = application === 'tournament';
	const isTeamLeaguesPage = application === 'team-leagues';
	const isWprPage = application === 'wpr-rankings';
	const isClubsPage = application === 'clubs';
	const isLeaguesPage = application === 'leagues';
	const isPickleball = application === 'pickleball' || isLeaguesPage || isWprPage;

	const topNavigationClasses = clsx('hidden text-white sm:block', {
		'!bg-[#EF612D]': isTeamLeaguesPage,
		'!bg-[#4CA30D]': isWprPage,
		'!bg-clubs-700': isClubsPage,
		'!bg-leagues-300': isLeaguesPage,
		'bg-experiment': !isTourneyPage && !isTeamLeaguesPage && !isWprPage && !isClubsPage && !isLeaguesPage,
		'!bg-blue-600': isTourneyPage
	});

	const pickleballCentralClasses = 'inline-flex items-center gap-2 pr-4 text-sm font-medium underline-offset-4 hover:underline decoration-white';

	const loginButtonClasses = clsx(
		'mr-2 flex items-center gap-2 !bg-transparent !py-1.5 text-sm font-medium !leading-none text-gray-700 hover:!bg-transparent',
		{
			'text-white hover:!text-white/80': !isTourneyPage,
			'text-white hover:!bg-transparent hover:!text-white/80': isTourneyPage,
			'cursor-pointer': onSSOClick
		}
	);

	const signUpClasses = clsx('rounded !py-1.5 px-2 text-sm font-medium !leading-none', {
		'!bg-white !text-[#EF6820] hover:!text-[#EF6820]': isTeamLeaguesPage,
		'!bg-white !text-[#4CA30D] hover:!text-[#4CA30D]': isWprPage,
		'!bg-white !text-error-700 hover:!text-error-700': isClubsPage,
		'!bg-white !text-leagues-300 hover:!text-leagues-300': isLeaguesPage,
		'!bg-white !text-experiment hover:!text-experiment': !isTourneyPage && !isTeamLeaguesPage && !isWprPage && !isClubsPage && !isLeaguesPage,
		'!bg-white !text-blue-600 hover:!text-blue-600': isTourneyPage
	});

	return (
		<div className={topNavigationClasses} id="pb-topnav">
			<ul className="mx-auto flex h-10 max-w-[1280px] items-center justify-between px-4">
				<li className="divide-x divide-gray-400">
					<CustomLink
						href="https://pickleballcentral.com"
						target="_blank"
						className={pickleballCentralClasses}
						data-testid={`pre-header-link-pickleball-central`}
					>
						<span className="inline-flex">Shop Pickleball Central</span>
						<FontAwesomeIcon icon={faExternalLink} size="sm" />
					</CustomLink>
				</li>
				<li className="divide-x divide-gray-400">
					<CustomLink
						href={`http${isLocalHost && isPickleball ? '' : 's'}://${pickleballHref}/watch-now`}
						className={pickleballCentralClasses}
						data-testid={`pre-header-link-watch-now`}
					>
						<span className="inline-flex">Where to Watch Pickleball</span>
					</CustomLink>
				</li>
				<li className="flex items-center gap-2" data-testid={`pre-header-user-profile`}>
					{user?.uuid ? (
						<>
							<NotificationsPopup notifications={notifications} checkoutLink={`${checkoutLink}/pending`} />
							<UserProfile
								cdnUrl={cdnUrl}
								pickleballHref={pickleballHref}
								tournamentHref={tournamentHref}
								leaguesHref={leaguesHref}
								clubsHref={clubsHref}
								teamLeaguesHref={teamLeaguesHref}
								ssoLink={ssoLink}
								user={user}
								canAccessManage={canAccessManage}
								isLocalhost={isLocalHost}
								pathname={pathname}
								isPickleball={isPickleball}
							/>
						</>
					) : (
						<>
							{onSSOClick && !isLocalHost ? (
								<div className={loginButtonClasses} rel="nofollow" data-testid={`pre-header-user-profile-login`} onClick={onSSOClick}>
									<FontAwesomeIcon icon={faUserCircle} className="text-lg" />
									Log In
								</div>
							) : isLocalHost ? (
								<CustomLink
									href={`/login?continue=${encodeURIComponent(baseURL)}`}
									className={loginButtonClasses}
									rel="nofollow"
									data-testid={`pre-header-user-profile-login`}
								>
									<FontAwesomeIcon icon={faUserCircle} className="text-lg" />
									Log In
								</CustomLink>
							) : (
								<a
									href={`${ssoLink}/?flow=SelfService&continue=${encodeURIComponent(baseURL)}`}
									className={loginButtonClasses}
									rel="nofollow"
									data-testid={`pre-header-user-profile-login`}
								>
									<FontAwesomeIcon icon={faUserCircle} className="text-lg" />
									Log In
								</a>
							)}
							<a
								href={`${ssoLink}/v3/signup?continue=${encodeURIComponent(baseURL)}`}
								className={signUpClasses}
								rel="nofollow"
								data-testid={`pre-header-user-profile-register`}
							>
								Sign Up
							</a>
						</>
					)}
				</li>
			</ul>
		</div>
	);
}
